/**
 * Shared HR-role RBAC helpers — the single source of truth for the 4-role
 * HR set, reused by middleware.ts, Sidebar.tsx, useHrDashboard, and
 * PermissionWrapper so the array isn't duplicated in 4 places. Plain
 * functions/arrays, same idiom as the existing onlySuperAdmin/onlyDeveloper
 * boolean flags — not a new abstraction layer.
 */

export const HR_ROLES = ["SUPERADMIN", "ADMIN", "COMPTABLE", "GERANT"] as const;

export type HrRole = (typeof HR_ROLES)[number];

/**
 * Narrower than HR_ROLES — for HR sub-resources that are adminOnly on the
 * backend (Devices, Command Center, Store Managers: SUPERADMIN/ADMIN only,
 * no GERANT/COMPTABLE). Layered onto HR_ROLES the same way HR_ROLES is
 * layered onto the general ADMIN_ROLES check in middleware.ts.
 */
export const HR_ADMIN_ROLES = ["SUPERADMIN", "ADMIN"] as const;

export type HrDashboardVariant = "admin" | "accounting" | "storeManager";

export function hasAnyRole(
  role: string | null | undefined,
  allowed: readonly string[]
): boolean {
  return !!role && allowed.includes(role);
}

export function isHrRole(role?: string | null): role is HrRole {
  return hasAnyRole(role, HR_ROLES);
}

/**
 * Resolves which /hr-dashboard/* variant a role is entitled to, matching
 * the backend's dashboard RBAC exactly (getAdminDashboard: SUPERADMIN/ADMIN,
 * getAccountingDashboard: SUPERADMIN/COMPTABLE, getStoreManagerDashboard:
 * SUPERADMIN/GERANT). SUPERADMIN resolves to "admin" (full picture).
 */
export function getHrDashboardVariant(
  role?: string | null
): HrDashboardVariant | null {
  if (hasAnyRole(role, ["SUPERADMIN", "ADMIN"])) return "admin";
  if (role === "COMPTABLE") return "accounting";
  if (role === "GERANT") return "storeManager";
  return null;
}
